import { useApi, useBearer } from '@healthblocks-io/core'
import Axios from 'axios'
import { useCallback, useEffect, useMemo } from 'react'
import { atom, useRecoilState, useResetRecoilState } from 'recoil'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'
import { capacitorPreferencesEffect } from '@/capacitor/recoil/capacitor-preferences-effect'

import type { ActorMember } from '@healthblocks-io/core'
import type { AxiosResponse } from 'axios'

const memberAtom = atom<ActorMember | undefined>({
  key: '@healthblocks/member',
  default: undefined,
  effects: [capacitorPreferencesEffect('@healthblocks/member')],
})

function useMember() {
  const [member, setMember] = useRecoilState(memberAtom)
  const reset = useResetRecoilState(memberAtom)

  const bearer = useBearer()

  const baseURL = useApi()
  const axios = useMemo(() => Axios.create({ baseURL }), [baseURL])

  // Methods
  // ------------------------------------------------------------------------- /
  const getMember = useCallback(
    async (id: number) => {
      const bearerTokenHeader = await bearer()

      return axios
        .get<ActorMember[]>('member', {
          headers: {
            ...bearerTokenHeader,
          },
          params: {
            user: 'me',
          },
        })
        .then((value) => {
          return {
            ...value,
            data: value.data.find((v) => v.userId === id),
          } as AxiosResponse<ActorMember>
        })
        .catch(() => {
          return {} as AxiosResponse<ActorMember>
        })
    },
    [axios, bearer],
  )

  const updateMember = useCallback(
    async (id: number, updates: Partial<ActorMember>) => {
      const bearerTokenHeader = await bearer()
      return await axios.patch<ActorMember>(`member/${id}`, updates, {
        headers: {
          ...bearerTokenHeader,
        },
        params: {
          user: 'me',
        },
      })
    },
    [axios, bearer],
  )

  const deleteMember = useCallback(
    async (id: number) => {
      const bearerTokenHeader = await bearer()
      return await axios.delete<ActorMember>(`member/${id}`, {
        headers: {
          ...bearerTokenHeader,
        },
      })
    },
    [axios, bearer],
  )

  const clearMember = useCallback(() => {
    setMember(undefined)
  }, [setMember])

  const getMemberAsync = useAxiosAsync(getMember)
  const updateMemberAsync = useAxiosAsync(updateMember)
  const deleteMemberAsync = useAxiosAsync(deleteMember)

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (getMemberAsync.value) {
      setMember(getMemberAsync.value)
    }
  }, [getMemberAsync.value])

  useEffect(() => {
    if (updateMemberAsync.value) {
      setMember(updateMemberAsync.value)
    }
  }, [updateMemberAsync.value])

  return {
    reset,

    member,
    clearMember,
    getMember: getMemberAsync,
    updateMember: updateMemberAsync,
    deleteMember: deleteMemberAsync,
  }
}

export { useMember }
